/*
  File: src/components/common/AddToDataset.jsx
  Purpose: Reusable component for adding detections/streams to datasets
  Props:
  - detectionId: single detection ID (for individual detection)
  - streamId: stream ID (for adding all stream detections)
  - mode: 'button' | 'dropdown' | 'full' - UI mode
  - compact: boolean - show compact version
  - onSuccess: callback when successfully added
*/
import React, { useState, useEffect } from 'react';

function AddToDataset({ 
    detectionId = null, 
    streamId = null, 
    mode = 'dropdown', 
    compact = false, 
    onSuccess = null,
    className = ''
}) {
    const [datasets, setDatasets] = useState([]);
    const [selectedDataset, setSelectedDataset] = useState('');
    const [loading, setLoading] = useState(false);
    const [datasetsLoading, setDatasetsLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    const rawBase = (process.env.REACT_APP_API_BASE || '').trim();
    const cleanedBase = rawBase.replace(/^@+/, '');
    const API_BASE = cleanedBase || '/dev';

    // Fetch available datasets
    useEffect(() => {
        const fetchDatasets = async () => {
            try {
                const res = await fetch(`${API_BASE}/rekog/datasets`, {
                    headers: { accept: 'application/json' },
                    credentials: 'include'
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                setDatasets(Array.isArray(data) ? data : []);
            } catch (e) {
                setError('Failed to load datasets');
            } finally {
                setDatasetsLoading(false);
            }
        };

        fetchDatasets();
    }, [API_BASE]);

    // Add to dataset
    const addToDataset = async () => {
        if (!selectedDataset) {
            setError('Please select a dataset');
            return;
        }

        setLoading(true);
        setError('');
        
        try {
            const endpoint = streamId 
                ? `${API_BASE}/rekog/datasets/${selectedDataset}/images/from-stream`
                : `${API_BASE}/rekog/datasets/${selectedDataset}/images/from-detection`;
            
            const payload = streamId 
                ? { stream_id: streamId }
                : { detection_id: detectionId };

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }

            const result = await res.json();
            
            if (streamId) {
                setSuccess(`Added ${result.images_created} images with ${result.annotations_created} annotations to dataset`);
            } else {
                setSuccess('Detection added to dataset successfully');
            }
            
            setSelectedDataset('');
            setShowDropdown(false);
            
            if (onSuccess) {
                onSuccess(result);
            }

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);

        } catch (e) {
            setError(e.message || 'Failed to add to dataset');
            setTimeout(() => setError(''), 5000);
        } finally {
            setLoading(false);
        }
    };

    if (datasetsLoading) {
        return (
            <div className={`${compact ? 'text-xs' : 'text-sm'} text-white/60 ${className}`}>
                Loading...
            </div>
        );
    }

    if (datasets.length === 0) {
        return (
            <div className={`${compact ? 'text-xs' : 'text-sm'} text-white/60 ${className}`}>
                No datasets
            </div>
        );
    }

    // Button mode - just shows a button that opens dropdown
    if (mode === 'button') {
        return (
            <div className={`relative ${className}`}>
                <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className={`${compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white font-medium transition-all duration-200`}
                    disabled={loading}
                >
                    {loading ? '...' : '+ Dataset'}
                </button>

                {showDropdown && (
                    <div className="absolute top-full mt-1 right-0 z-50 w-64 bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-lg border border-[#4cc3e9]/30 p-3 shadow-xl">
                        <div className="mb-2">
                            <label className="block text-xs font-medium text-white/80 mb-1">
                                Select Dataset
                            </label>
                            <select
                                value={selectedDataset}
                                onChange={(e) => setSelectedDataset(e.target.value)}
                                className="w-full px-2 py-1 rounded bg-white/10 border border-white/20 text-white text-sm [&>option]:bg-gray-800 [&>option]:text-white"
                            >
                                <option value="" className="bg-gray-800 text-white">Choose dataset...</option>
                                {datasets.map(dataset => (
                                    <option key={dataset.id} value={dataset.id} className="bg-gray-800 text-white">
                                        {dataset.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <button
                                onClick={addToDataset}
                                disabled={loading || !selectedDataset}
                                className="flex-1 px-3 py-1.5 rounded bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Adding...' : 'Add'}
                            </button>
                            <button
                                onClick={() => setShowDropdown(false)}
                                className="px-2 py-1.5 rounded bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        
                        {error && (
                            <div className="mt-2 text-red-400 text-xs">
                                {error}
                            </div>
                        )}
                        
                        {success && (
                            <div className="mt-2 text-green-400 text-xs">
                                {success}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Full mode - shows complete interface
    return (
        <div className={`space-y-3 ${className}`}>
            <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                    Add {streamId ? 'Stream' : 'Detection'} to Dataset
                </label>
                <select
                    value={selectedDataset}
                    onChange={(e) => setSelectedDataset(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white [&>option]:bg-gray-800 [&>option]:text-white"
                    disabled={loading}
                >
                    <option value="" className="bg-gray-800 text-white">Select a dataset...</option>
                    {datasets.map(dataset => (
                        <option key={dataset.id} value={dataset.id} className="bg-gray-800 text-white">
                            {dataset.name} ({dataset.images_count || 0} images)
                        </option>
                    ))}
                </select>
            </div>

            <button
                onClick={addToDataset}
                disabled={loading || !selectedDataset}
                className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {loading ? 'Adding...' : `Add ${streamId ? 'All Stream Detections' : 'Detection'} to Dataset`}
            </button>

            {error && (
                <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded p-2">
                    {error}
                </div>
            )}

            {success && (
                <div className="text-green-400 text-sm bg-green-500/10 border border-green-500/20 rounded p-2">
                    {success}
                </div>
            )}
        </div>
    );
}

export default AddToDataset;